import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useExploreCity } from "@/contexts/ExploreCityContext";
import { categoryEmoji, type Rarity, rarityColour } from "@/lib/achievementEngine";
import { RestaurantDetailSheet } from "@/components/forkd/RestaurantDetailSheet";
import { RestaurantPhoto } from "@/components/forkd/RestaurantPhoto";

type Cuisine = {
  id: string;
  name: string;
  slug: string;
  emoji: string | null;
  parent_id: string | null;
};

type Restaurant = {
  id: string;
  name: string;
  photo_url: string | null;
  google_place_id: string | null;
  visit_count: number;
  neighbourhood: { name: string } | null;
  cuisines: { name: string; emoji: string | null }[];
};

type Achievement = {
  id: string;
  name: string;
  description: string;
  rarity: Rarity;
  colour_hex: string;
  category: string;
  trigger_params: any;
};

export default function CuisineDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { cityId: exploreCityId } = useExploreCity();
  const [cuisine, setCuisine] = useState<Cuisine | null>(null);
  const [parentCuisine, setParentCuisine] = useState<Cuisine | null>(null);
  const [cityName, setCityName] = useState<string>("");
  const [cityId, setCityId] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [visitedIds, setVisitedIds] = useState<Set<string>>(new Set());
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [earnedIds, setEarnedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [openRestaurant, setOpenRestaurant] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: ct } = await supabase
        .from("cuisine_types")
        .select("id, name, slug, emoji, parent_id")
        .eq("slug", slug)
        .maybeSingle();
      if (!ct || cancelled) {
        setLoading(false);
        return;
      }
      setCuisine(ct as Cuisine);

      // Parent
      if ((ct as any).parent_id) {
        const { data: parent } = await supabase
          .from("cuisine_types")
          .select("id, name, slug, emoji, parent_id")
          .eq("id", (ct as any).parent_id)
          .maybeSingle();
        if (!cancelled) setParentCuisine((parent as Cuisine) ?? null);
      } else {
        setParentCuisine(null);
      }

      // City: selected explore city first, then profile home, then London
      let resolvedCityId: string | null = null;
      let resolvedCityName = "";
      if (exploreCityId) {
        resolvedCityId = exploreCityId;
      } else if (user) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("home_city_id")
          .eq("id", user.id)
          .maybeSingle();
        resolvedCityId = (prof?.home_city_id as string) ?? null;
      }
      if (!resolvedCityId) {
        const { data: london } = await supabase
          .from("cities")
          .select("id, name")
          .eq("name", "London")
          .maybeSingle();
        resolvedCityId = (london?.id as string) ?? null;
        resolvedCityName = (london?.name as string) ?? "";
      } else {
        const { data: c } = await supabase
          .from("cities")
          .select("name")
          .eq("id", resolvedCityId)
          .maybeSingle();
        resolvedCityName = (c?.name as string) ?? "";
      }
      if (cancelled) return;
      setCityId(resolvedCityId);
      setCityName(resolvedCityName);

      // Cuisine ids: this cuisine + any children (only relevant if parent)
      const { data: children } = await supabase
        .from("cuisine_types")
        .select("id")
        .eq("parent_id", (ct as any).id);
      const cuisineIds = [
        (ct as any).id,
        ...((children ?? []) as any[]).map((x) => x.id),
      ];

      // Restaurants
      let restRows: Restaurant[] = [];
      if (resolvedCityId) {
        const { data: rels } = await supabase
          .from("restaurant_cuisine_types")
          .select(
            "restaurants!inner(id, name, photo_url, google_place_id, visit_count, city_id, neighbourhood:neighbourhoods(name), restaurant_cuisine_types(cuisine_types(name, emoji)))",
          )
          .in("cuisine_type_id", cuisineIds)
          .eq("restaurants.city_id", resolvedCityId);

        const seen = new Set<string>();
        for (const row of (rels ?? []) as any[]) {
          const r = row.restaurants;
          if (!r || seen.has(r.id)) continue;
          seen.add(r.id);
          restRows.push({
            id: r.id,
            name: r.name,
            photo_url: r.photo_url,
            google_place_id: r.google_place_id,
            visit_count: r.visit_count,
            neighbourhood: r.neighbourhood,
            cuisines: (r.restaurant_cuisine_types ?? [])
              .map((x: any) => x.cuisine_types)
              .filter(Boolean),
          });
        }
        restRows.sort((a, b) => b.visit_count - a.visit_count);
      }
      if (!cancelled) setRestaurants(restRows);

      // User's visited restaurants
      if (user && restRows.length > 0) {
        const { data: myVisits } = await supabase
          .from("visits")
          .select("restaurant_id")
          .eq("user_id", user.id)
          .in(
            "restaurant_id",
            restRows.map((r) => r.id),
          );
        const ids = new Set<string>(
          ((myVisits ?? []) as any[]).map((v) => v.restaurant_id),
        );
        if (!cancelled) setVisitedIds(ids);
      }

      // Achievements related to this cuisine (first-bite + devotee/connoisseur)
      const { data: ach } = await supabase
        .from("achievements")
        .select("id, name, description, rarity, colour_hex, category, trigger_params")
        .eq("category", "cuisine")
        .eq("is_active", true);
      const related = ((ach ?? []) as Achievement[]).filter((a) => {
        const p = a.trigger_params ?? {};
        return p.cuisine_slug === (ct as any).slug;
      });
      if (!cancelled) setAchievements(related);

      // Earned ids
      if (user) {
        const { data: earned } = await supabase
          .from("user_achievements")
          .select("achievement_id")
          .eq("user_id", user.id);
        if (!cancelled)
          setEarnedIds(
            new Set<string>(((earned ?? []) as any[]).map((e) => e.achievement_id)),
          );
      }

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, user, exploreCityId]);

  const visitedCount = visitedIds.size;
  const total = restaurants.length;
  const pct = total > 0 ? Math.round((visitedCount / total) * 100) : 0;

  if (loading) {
    return (
      <div className="px-4 pt-4 space-y-4">
        <div
          className="h-8 w-1/2 rounded animate-pulse"
          style={{ backgroundColor: "var(--colour-surface-raised)" }}
        />
        <div
          className="h-24 rounded-[var(--radius-md)] animate-pulse"
          style={{ backgroundColor: "var(--colour-surface-raised)" }}
        />
      </div>
    );
  }

  if (!cuisine) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-[var(--colour-text-secondary)]">Cuisine not found.</p>
      </div>
    );
  }

  return (
    <div className="pb-8">
      <header className="h-14 px-2 flex items-center">
        <button
          onClick={() => nav(-1)}
          className="w-11 h-11 flex items-center justify-center"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
      </header>

      {/* Hero */}
      <section className="px-5 text-center">
        <div className="text-[64px] leading-none">{cuisine.emoji ?? "🍽️"}</div>
        <h1 className="mt-2 text-[28px] font-bold">{cuisine.name}</h1>
        {parentCuisine && (
          <p className="text-[13px] text-[var(--colour-text-secondary)]">
            in {parentCuisine.name}
          </p>
        )}
        <p className="mt-1 text-[14px] text-[var(--colour-text-secondary)]">
          {total} restaurant{total === 1 ? "" : "s"}
          {cityName ? ` in ${cityName}` : ""}
        </p>
      </section>

      {/* Progress */}
      {user && total > 0 && (
        <section className="px-4 mt-5">
          <div
            className="p-4 rounded-[var(--radius-md)] border"
            style={{
              borderColor: "var(--colour-border)",
              backgroundColor: "var(--colour-surface)",
            }}
          >
            <p className="text-[14px] font-semibold">
              {visitedCount} of {total} {cuisine.name} restaurant
              {total === 1 ? "" : "s"} visited
            </p>
            <div
              className="mt-2 h-2 rounded-full overflow-hidden"
              style={{ backgroundColor: "var(--colour-surface-raised)" }}
            >
              <div
                className="h-full"
                style={{
                  width: `${pct}%`,
                  backgroundColor: "var(--colour-accent)",
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Achievements */}
      {achievements.length > 0 && (
        <section className="px-4 mt-6">
          <h2 className="text-[16px] font-semibold mb-3">Related badges</h2>
          <div className="grid grid-cols-2 gap-2.5">
            {achievements.map((a) => {
              const earned = earnedIds.has(a.id);
              return (
                <div
                  key={a.id}
                  className="p-3 rounded-[var(--radius-md)] border flex flex-col items-center text-center gap-1"
                  style={{
                    borderColor: earned ? a.colour_hex : "var(--colour-border)",
                    backgroundColor: "var(--colour-surface)",
                    opacity: earned ? 1 : 0.55,
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-[var(--radius-full)] flex items-center justify-center text-[22px]"
                    style={{
                      backgroundColor: earned
                        ? a.colour_hex + "22"
                        : "var(--colour-surface-raised)",
                      border: earned
                        ? `2px solid ${a.colour_hex}`
                        : "2px solid var(--colour-border)",
                    }}
                  >
                    {earned ? (
                      categoryEmoji(a.category)
                    ) : (
                      <Lock size={16} className="text-[var(--colour-text-secondary)]" />
                    )}
                  </div>
                  <div
                    className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: rarityColour(a.rarity) }}
                  >
                    {a.rarity}
                  </div>
                  <div className="text-[13px] font-semibold">{a.name}</div>
                  <div className="text-[11px] text-[var(--colour-text-secondary)] line-clamp-2">
                    {a.description}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Restaurants */}
      <section className="px-4 mt-6">
        <h2 className="text-[16px] font-semibold mb-3">Restaurants</h2>
        {restaurants.length === 0 ? (
          <p className="text-[13px] text-[var(--colour-text-secondary)]">
            No restaurants for this cuisine in {cityName} yet.
          </p>
        ) : (
          <ul className="space-y-2.5">
            {restaurants.map((r) => {
              const visited = visitedIds.has(r.id);
              return (
                <li key={r.id}>
                  <button
                    onClick={() => setOpenRestaurant(r.id)}
                    className="w-full text-left flex items-center gap-3 p-2.5 rounded-[var(--radius-md)] border"
                    style={{
                      borderColor: "var(--colour-border)",
                      backgroundColor: "var(--colour-surface)",
                    }}
                  >
                    <div
                      className="w-14 h-14 shrink-0 rounded-[var(--radius-md)] overflow-hidden flex items-center justify-center text-[26px]"
                      style={{ backgroundColor: "var(--colour-surface-raised)" }}
                    >
                      <RestaurantPhoto
                        restaurantId={r.id}
                        photoUrl={r.photo_url}
                        googlePlaceId={r.google_place_id}
                        emoji={r.cuisines[0]?.emoji ?? cuisine.emoji ?? "🍽️"}
                        alt={r.name}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold truncate flex items-center gap-1.5">
                        {r.name}
                        {visited && (
                          <span
                            className="text-[12px]"
                            style={{ color: "var(--colour-accent)" }}
                            aria-label="Visited"
                            title="Visited"
                          >
                            🍴
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                        {r.cuisines[0]?.emoji ?? ""} {r.cuisines[0]?.name ?? cuisine.name}
                        {r.neighbourhood?.name ? ` · ${r.neighbourhood.name}` : ""}
                      </div>
                      <div className="text-[11px] text-[var(--colour-text-secondary)] flex items-center gap-1 mt-0.5">
                        <Users size={11} />
                        {r.visit_count} visit{r.visit_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <RestaurantDetailSheet
        restaurantId={openRestaurant}
        open={openRestaurant !== null}
        onClose={() => setOpenRestaurant(null)}
      />
    </div>
  );
}
